import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
// import MainLayout from '../../components/layout/MainLayout';
import {
  ArrowLeft, Save, Send, User, Calendar, MessageSquare,
  CheckCircle2, AlertCircle, Download, Target, Flag, Star, ShieldCheck, Loader2, FileText,
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
// RadioGroup replaced with custom rating pills for Self-Review
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
// import {
//   getEmployeeAppraisal, getAppraisalReport, getTemplate, getEmployee,
//   submitSelfReview, submitHRReview, submitEmployeeComments, submitHRSignOff,
//   getTemplates, createEmployeeAppraisal,
// } from '../../lib/api';
import { generateAppraisalPDF } from '../../../lib/pdfExport';

const STEPS = ["Created", "Self-Review", "HR Review", "Employee Comments", "HR Sign-off", "Completed"];

const stepIndex = (status) => {
  const i = STEPS.indexOf(status);
  return i >= 0 ? i : 0;
};

const StepIndicator = ({ currentStatus }) => {
  const displaySteps = STEPS.slice(0, 5);
  const currentIdx = stepIndex(currentStatus);
  return (
    <div className="flex items-center gap-0 py-4" data-testid="appraisal-step-indicator">
      {displaySteps.map((step, i) => (
        <div key={i} className="flex items-center">
          <div className="flex flex-col items-center gap-1 shrink-0 min-w-[90px]">
            <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
              i < currentIdx || currentStatus === 'Completed'
                ? 'bg-primary border-primary text-white'
                : i === currentIdx
                  ? 'bg-primary/20 border-primary text-primary ring-4 ring-primary/10'
                  : 'bg-card border-border text-muted-foreground'
            }`}>
              {i < currentIdx || currentStatus === 'Completed' ? <CheckCircle2 className="h-5 w-5" /> : i + 1}
            </div>
            <span className={`text-[11px] text-center leading-tight ${
              i === currentIdx ? 'text-primary font-semibold' : i < currentIdx || currentStatus === 'Completed' ? 'text-primary font-medium' : 'text-muted-foreground'
            }`}>
              {step}
            </span>
          </div>
          {i < displaySteps.length - 1 && (
            <div className={`h-0.5 w-12 -mt-4 shrink-0 ${i < currentIdx || currentStatus === 'Completed' ? 'bg-primary' : 'bg-border'}`} />
          )}
        </div>
      ))}
    </div>
  );
};

const exportToPDF = async (appraisalId) => {
  try {
    toast.info("Generating PDF report...");
    // Temporarily disabled - API not available
    // const report = await getAppraisalReport(appraisalId);
    // const fileName = await generateAppraisalPDF(report);
    // toast.success(`PDF saved: ${fileName}`);
    toast.info("PDF export temporarily disabled");
  } catch {
    toast.error("Failed to export PDF");
  }
};

const AppraisalEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';
  const employeeIdParam = searchParams.get('employeeId');

  const [appraisal, setAppraisal] = useState(null);
  const [template, setTemplate] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // New appraisal creation state
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [cycleName, setCycleName] = useState('');
  const [creating, setCreating] = useState(false);

  // Form state
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [starScores, setStarScores] = useState({});
  const [employeeComment, setEmployeeComment] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [finalRating, setFinalRating] = useState("");
  const [signOffComments, setSignOffComments] = useState("");

  // Draft persistence key
  const draftKey = id && id !== 'new' ? `appraisal-draft-${id}` : null;

  // Save draft to localStorage on form changes
  useEffect(() => {
    if (!draftKey || !appraisal) return;
    const draft = { ratings, comments, starScores, employeeComment, acknowledged, finalRating, signOffComments };
    localStorage.setItem(draftKey, JSON.stringify(draft));
  }, [ratings, comments, starScores, employeeComment, acknowledged, finalRating, signOffComments, draftKey, appraisal]);

  // Restore draft from localStorage
  const restoreDraft = useCallback((key) => {
    try {
      const saved = localStorage.getItem(key);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.ratings) setRatings(draft.ratings);
        if (draft.comments) setComments(draft.comments);
        if (draft.starScores) setStarScores(draft.starScores);
        if (draft.employeeComment) setEmployeeComment(draft.employeeComment);
        if (draft.acknowledged) setAcknowledged(draft.acknowledged);
        if (draft.finalRating) setFinalRating(draft.finalRating);
        if (draft.signOffComments) setSignOffComments(draft.signOffComments);
        return true;
      }
    } catch {}
    return false;
  }, []);

  useEffect(() => {
    if (isNew) {
      loadNewAppraisalData();
    } else {
      loadAppraisal();
    }
  }, [id]);

  const loadNewAppraisalData = async () => {
    try {
      // Temporarily disabled - API not available
      // const tmplList = await getTemplates();
      // setTemplates(tmplList.filter(t => t.type === 'Appraisal'));
      setTemplates([]); // Empty template list for now
      
      if (employeeIdParam) {
        // const emp = await getEmployee(employeeIdParam).catch(() => null);
        // setEmployee(emp);
        // if (emp) setCycleName(`${emp.name} — Performance Review`);
        // Mock employee data for now
        setEmployee({ name: 'Employee', designation: 'Role', department: 'Dept' });
        setCycleName('Employee — Performance Review');
      }
    } catch {
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAppraisal = async () => {
    if (!selectedTemplateId) { toast.error("Please select a template"); return; }
    if (!employeeIdParam) { toast.error("No employee selected"); return; }
    setCreating(true);
    try {
      // Temporarily disabled - API not available
      // const result = await createEmployeeAppraisal({
      //   employeeId: employeeIdParam,
      //   templateId: selectedTemplateId,
      //   cycleName: cycleName || undefined,
      // });
      // toast.success("Appraisal created! Starting Self-Review.");
      // navigate(`/performance/appraisal/${result.id}`, { replace: true });
      toast.info("Appraisal creation temporarily disabled");
    } catch (err) {
      toast.error(err.message || "Failed to create appraisal");
    } finally {
      setCreating(false);
    }
  };

  const loadAppraisal = async () => {
    try {
      // Temporarily disabled - API not available
      // const apprData = await getEmployeeAppraisal(id);
      // setAppraisal(apprData);
      
      // Mock appraisal data for UI testing
      const mockAppraisal = {
        id: id,
        cycleName: 'Mock Appraisal Cycle',
        employeeName: 'John Doe',
        employeeId: 'EMP001',
        department: 'Engineering',
        status: 'Self-Review',
        selfReview: null,
        hrReview: null,
        employeeComments: null,
        hrSignOff: null,
        templateId: null
      };
      setAppraisal(mockAppraisal);

      // Restore draft after loading appraisal
      const key = `appraisal-draft-${id}`;
      restoreDraft(key);

      // if (apprData.templateId) {
      //   const tmpl = await getTemplate(apprData.templateId).catch(() => null);
      //   setTemplate(tmpl);
      // }
      // if (apprData.employeeId) {
      //   const emp = await getEmployee(apprData.employeeId).catch(() => null);
      //   setEmployee(emp);
      // }
    } catch {
      toast.error("Failed to load appraisal");
    } finally {
      setLoading(false);
    }
  };

  const ratingOptions = template?.ratingOptions || ["Exceeds Expectations", "Meets Expectations", "Needs Improvement", "Unacceptable"];
  const sections = template?.sections || [];

  const allQuestionIds = sections.flatMap(s => (s.questions || []).map(q => q.id));
  const answeredCount = Object.keys(ratings).filter(k => allQuestionIds.includes(k) && ratings[k]).length;
  const progressPct = allQuestionIds.length > 0 ? Math.round((answeredCount / allQuestionIds.length) * 100) : 0;

  const buildResponses = () =>
    allQuestionIds.map(qId => ({
      questionId: qId,
      rating: ratings[qId] || "",
      comment: comments[qId] || "",
      starScore: starScores[qId] || 0,
    }));

  const handleSubmitSelfReview = async () => {
    setSubmitting(true);
    try {
      // Temporarily disabled - API not available
      // const updated = await submitSelfReview(id, { responses: buildResponses() });
      // setAppraisal(updated);
      // setRatings({}); setComments({}); setStarScores({});
      // if (draftKey) localStorage.removeItem(draftKey);
      // toast.success("Self-Review submitted! Moved to HR Review.");
      toast.info("Self-Review submission temporarily disabled");
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleSubmitHRReview = async () => {
    const score = answeredCount > 0
      ? parseFloat((Object.values(starScores).reduce((s, v) => s + v, 0) / Object.values(starScores).length).toFixed(1))
      : 0;
    setSubmitting(true);
    try {
      // Temporarily disabled - API not available
      // const updated = await submitHRReview(id, { responses: buildResponses(), overallScore: score });
      // setAppraisal(updated);
      // setRatings({}); setComments({}); setStarScores({});
      // if (draftKey) localStorage.removeItem(draftKey);
      // toast.success("HR Review submitted! Moved to Employee Comments.");
      toast.info("HR Review submission temporarily disabled");
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleSubmitEmployeeComments = async () => {
    setSubmitting(true);
    try {
      // Temporarily disabled - API not available
      // const updated = await submitEmployeeComments(id, { comments: employeeComment, acknowledged });
      // setAppraisal(updated);
      // if (draftKey) localStorage.removeItem(draftKey);
      // toast.success("Comments submitted! Moved to HR Sign-off.");
      toast.info("Employee comments submission temporarily disabled");
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  const handleSubmitHRSignOff = async () => {
    setSubmitting(true);
    try {
      // Temporarily disabled - API not available
      // const updated = await submitHRSignOff(id, { finalRating: parseFloat(finalRating) || 0, comments: signOffComments, signedBy: "Jane Doe" });
      // setAppraisal(updated);
      // if (draftKey) localStorage.removeItem(draftKey);
      // toast.success("Appraisal signed off and completed!");
      toast.info("HR Sign-off temporarily disabled");
    } catch (err) { toast.error(err.message); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!appraisal && !isNew) {
    return (
      <>
        <div className="text-center py-20 text-muted-foreground">
          <p className="text-lg">Appraisal not found.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/performance/my-appraisals')}>Back to Cycles</Button>
        </div>
      </>
    );
  }

  // --- New Appraisal Creation Flow ---
  if (isNew) {
    return (
      <>
        <div className="max-w-2xl mx-auto py-8">
          <div className="flex items-center gap-4 mb-8">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-from-new">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-foreground" data-testid="new-appraisal-title">Start New Appraisal</h1>
              <p className="text-sm text-muted-foreground">Create a performance review for {employee?.name || 'an employee'}</p>
            </div>
          </div>

          <Card>
            <CardHeader className="border-b bg-muted/50">
              <CardTitle className="text-base flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Appraisal Setup</CardTitle>
              <CardDescription>Select a template and give this review a name.</CardDescription>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Employee info */}
              {employee && (
                <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50 border">
                  <Avatar className="h-12 w-12">
                    <AvatarFallback className="bg-primary/10 text-primary font-bold">
                      {employee.name.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="font-semibold text-foreground">{employee.name}</p>
                    <p className="text-sm text-muted-foreground">{employee.designation} — {employee.department}</p>
                  </div>
                </div>
              )}

              {!employeeIdParam && (
                <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                  No employee selected. Please start an appraisal from an employee's profile.
                </div>
              )}

              {/* Cycle Name */}
              <div className="space-y-2">
                <Label className="font-medium">Review Name</Label>
                <Input
                  value={cycleName}
                  onChange={(e) => setCycleName(e.target.value)}
                  placeholder="e.g., Q1 2025 Performance Review"
                  data-testid="new-appraisal-name"
                />
              </div>

              {/* Template Selection */}
              <div className="space-y-3">
                <Label className="font-medium">Select Appraisal Template</Label>
                {templates.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No appraisal templates found. Create one in the Appraisal Builder first.</p>
                ) : (
                  <div className="grid gap-3">
                    {templates.map((tmpl) => (
                      <div
                        key={tmpl.id}
                        onClick={() => setSelectedTemplateId(tmpl.id)}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                          selectedTemplateId === tmpl.id
                            ? 'border-primary bg-primary/5 ring-2 ring-primary/10'
                            : 'border-border hover:border-primary/40 hover:bg-muted/50'
                        }`}
                        data-testid={`template-option-${tmpl.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-semibold text-foreground">{tmpl.name}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {tmpl.sections?.length || 0} sections · {tmpl.sections?.reduce((s, sec) => s + (sec.questions?.length || 0), 0) || 0} questions · v{tmpl.version}
                            </p>
                          </div>
                          <div className={`h-5 w-5 rounded-full border-2 flex items-center justify-center ${
                            selectedTemplateId === tmpl.id ? 'border-primary bg-primary' : 'border-border'
                          }`}>
                            {selectedTemplateId === tmpl.id && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                          </div>
                        </div>
                        {tmpl.ratingOptions?.length > 0 && (
                          <div className="flex gap-1.5 mt-2 flex-wrap">
                            {tmpl.ratingOptions.map((opt, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] font-normal">{opt}</Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="border-t p-4 flex justify-end gap-3">
              <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button
                className="bg-primary hover:bg-primary/90 gap-2"
                onClick={handleCreateAppraisal}
                disabled={creating || !selectedTemplateId || !employeeIdParam}
                data-testid="create-appraisal-btn"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Create & Start Review
              </Button>
            </CardFooter>
          </Card>
        </div>
      </>
    );
  }

  const status = appraisal.status;
  const isCompleted = status === "Completed";

  // Determine which submit handler/label to use
  const getSubmitAction = () => {
    switch (status) {
      case "Self-Review": return { label: "Submit Self-Review", icon: Send, handler: handleSubmitSelfReview, color: "bg-primary hover:bg-primary/90" };
      case "HR Review": return { label: "Submit HR Review", icon: Send, handler: handleSubmitHRReview, color: "bg-purple-600 hover:bg-purple-700" };
      case "Employee Comments": return { label: "Submit Comments", icon: MessageSquare, handler: handleSubmitEmployeeComments, color: "bg-amber-600 hover:bg-amber-700" };
      case "HR Sign-off": return { label: "Sign Off & Complete", icon: ShieldCheck, handler: handleSubmitHRSignOff, color: "bg-green-600 hover:bg-green-700" };
      default: return null;
    }
  };
  const submitAction = getSubmitAction();

  return (
    <>
      <div className="max-w-5xl mx-auto pb-20">
        {/* Header */}
        <div className="flex items-center gap-4 mb-2">
          <Button variant="ghost" size="icon" onClick={() => navigate('/performance/my-appraisals')} data-testid="back-to-cycles">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="appraisal-title">
              {appraisal.cycleName || "Appraisal"} — {appraisal.employeeName}
              <Badge variant="outline" className={`ml-2 text-xs ${
                isCompleted ? 'border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-950/30' :
                status === 'Self-Review' ? 'border-blue-200 text-blue-700 bg-blue-50 dark:border-blue-800 dark:text-blue-300 dark:bg-blue-950/30' :
                status === 'HR Review' ? 'border-purple-200 text-purple-700 bg-purple-50 dark:border-purple-800 dark:text-purple-300 dark:bg-purple-950/30' :
                status === 'Employee Comments' ? 'border-amber-200 text-amber-700 bg-amber-50 dark:border-amber-800 dark:text-amber-300 dark:bg-amber-950/30' :
                'border-orange-200 text-orange-700 bg-orange-50 dark:border-orange-800 dark:text-orange-300 dark:bg-orange-950/30'
              }`}>
                {status}
              </Badge>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            {(status === "Self-Review" || status === "HR Review") && (
              <span className="text-sm font-medium text-muted-foreground mr-2">{progressPct}%</span>
            )}
            <Button variant="outline" size="sm" onClick={() => exportToPDF(appraisal.id)} data-testid="export-pdf-btn">
              <Download className="mr-2 h-4 w-4" /> PDF
            </Button>
            {submitAction && !isCompleted && (
              <Button className={`${submitAction.color} gap-2`} onClick={submitAction.handler} disabled={submitting} data-testid="submit-step-btn">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <submitAction.icon className="h-4 w-4" />}
                {submitAction.label}
              </Button>
            )}
          </div>
        </div>

        {/* Step Indicator */}
        <div className="flex justify-center mb-6">
          <StepIndicator currentStatus={status} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" id="appraisal-content">
          {/* Left Sidebar */}
          <div className="lg:col-span-3 space-y-4">
            <Card>
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <Avatar className="h-20 w-20 mb-3">
                  <AvatarFallback className="text-lg bg-primary/10 text-primary font-bold">
                    {appraisal.employeeName.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <h2 className="font-bold text-lg">{appraisal.employeeName}</h2>
                <p className="text-sm text-muted-foreground">{employee?.designation || appraisal.department}</p>
                <div className="mt-4 w-full space-y-2 text-left text-sm">
                  <div className="flex justify-between py-1 border-b"><span className="text-muted-foreground">ID</span><span className="font-medium">{appraisal.employeeId}</span></div>
                  <div className="flex justify-between py-1 border-b"><span className="text-muted-foreground">Department</span><span className="font-medium">{appraisal.department}</span></div>
                  {employee?.manager && <div className="flex justify-between py-1 border-b"><span className="text-muted-foreground">Manager</span><span className="font-medium">{employee.manager}</span></div>}
                  {employee?.joinDate && <div className="flex justify-between py-1 border-b"><span className="text-muted-foreground">Joined</span><span className="font-medium">{employee.joinDate}</span></div>}
                </div>
              </CardContent>
            </Card>

            {/* Tips card based on step */}
            {status === "Self-Review" && (
              <Card className="border-blue-200 bg-blue-50/50 dark:border-blue-800 dark:bg-blue-950/30">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-blue-800 dark:text-blue-300 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> Self-Review Tips</CardTitle></CardHeader>
                <CardContent className="text-xs text-blue-700 dark:text-blue-400 space-y-1.5">
                  <p>Rate yourself honestly on each question.</p>
                  <p>Provide examples to support your ratings.</p>
                  <p>Focus on measurable achievements.</p>
                </CardContent>
              </Card>
            )}
            {status === "HR Review" && (
              <Card className="border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/30">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-purple-800 dark:text-purple-300 flex items-center gap-2"><AlertCircle className="h-4 w-4" /> HR Review Tips</CardTitle></CardHeader>
                <CardContent className="text-xs text-purple-700 dark:text-purple-400 space-y-1.5">
                  <p>Compare against self-review responses.</p>
                  <p>Use consistent rating criteria.</p>
                  <p>Provide constructive, specific feedback.</p>
                </CardContent>
              </Card>
            )}

            {/* Previous step data summary */}
            {appraisal.selfReview?.submittedAt && status !== "Self-Review" && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">Self-Review</CardTitle></CardHeader>
                <CardContent className="text-xs">
                  <Badge variant="outline" className="border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-950/30 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> Submitted</Badge>
                  <p className="text-muted-foreground mt-1">{appraisal.selfReview.responses?.length || 0} responses</p>
                </CardContent>
              </Card>
            )}
            {appraisal.hrReview?.submittedAt && status !== "HR Review" && status !== "Self-Review" && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-xs text-muted-foreground">HR Review</CardTitle></CardHeader>
                <CardContent className="text-xs">
                  <Badge variant="outline" className="border-green-200 text-green-700 bg-green-50 dark:border-green-800 dark:text-green-300 dark:bg-green-950/30 text-[10px]"><CheckCircle2 className="h-3 w-3 mr-1" /> Submitted</Badge>
                  <p className="text-muted-foreground mt-1">Score: {appraisal.hrReview.overallScore}/5</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Main Content */}
          <div className="lg:col-span-9 space-y-16">

            {/* Completed Banner */}
            {isCompleted && (
              <Card className="border-green-200 bg-green-50/50 dark:border-green-800 dark:bg-green-950/30">
                <CardContent className="py-6 flex items-center gap-4">
                  <CheckCircle2 className="h-10 w-10 text-green-600" />
                  <div>
                    <h3 className="text-lg font-bold text-green-800 dark:text-green-300">Appraisal Complete</h3>
                    <p className="text-sm text-green-700 dark:text-green-400">Final Rating: <strong>{appraisal.hrSignOff?.finalRating || "N/A"}/5</strong> | Signed by: {appraisal.hrSignOff?.signedBy || "N/A"}</p>
                    {appraisal.hrSignOff?.comments && <p className="text-xs text-green-600 dark:text-green-500 mt-1">"{appraisal.hrSignOff.comments}"</p>}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* SELF-REVIEW or HR REVIEW: Show template questions */}
            {(status === "Self-Review" || status === "HR Review") && sections.length > 0 && (
              <>
                {/* Completion Progress Banner */}
                <div className="rounded-xl border bg-card p-5" data-testid="review-progress-banner">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Target className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">
                          {status === "Self-Review" ? "Self-Review Progress" : "HR Review Progress"}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          {answeredCount} of {allQuestionIds.length} questions answered
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-primary">{progressPct}%</span>
                    </div>
                  </div>
                  <Progress value={progressPct} className="h-2" />
                </div>

                {/* Top summary card removed — self-review responses shown inline per question */}

                {sections.map((section, sectionIndex) => {
                  const sectionQuestionIds = (section.questions || []).map(q => q.id);
                  const sectionAnswered = sectionQuestionIds.filter(qId => ratings[qId]).length;
                  const sectionTotal = sectionQuestionIds.length;
                  const sectionPct = sectionTotal > 0 ? Math.round((sectionAnswered / sectionTotal) * 100) : 0;

                  return (
                    <div key={section.id} className="space-y-8" data-testid={`form-section-${section.id}`}>
                      {/* Section Header */}
                      <div className="relative rounded-xl border bg-card overflow-hidden">
                        <div className="section-header-accent" />
                        <div className="pl-5 pr-5 py-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex items-start gap-3.5">
                              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                <span className="text-sm font-bold text-primary">{sectionIndex + 1}</span>
                              </div>
                              <div>
                                <h3 className="text-base font-semibold text-foreground leading-tight">{section.title}</h3>
                                {section.description && (
                                  <p className="text-sm text-muted-foreground mt-1">{section.description}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              {section.weight > 0 && (
                                <Badge className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/10 text-xs font-semibold px-3 py-1">
                                  {section.weight}% Weight
                                </Badge>
                              )}
                            </div>
                          </div>
                          {/* Section progress */}
                          <div className="mt-4 flex items-center gap-3">
                            <Progress value={sectionPct} className="h-1.5 flex-1" />
                            <span className="text-xs font-medium text-muted-foreground whitespace-nowrap">
                              {sectionAnswered}/{sectionTotal}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Questions */}
                      {(section.questions || []).map((question, qi) => {
                        const selectedRating = ratings[question.id] || "";
                        const currentStars = starScores[question.id] || 0;
                        const selfResp = status === "HR Review" ? appraisal.selfReview?.responses?.find(r => r.questionId === question.id) : null;

                        const getRatingStyle = (option, isSelected) => {
                          if (!isSelected) {
                            return "border-border bg-card text-muted-foreground hover:border-primary/40 hover:bg-primary/5 dark:hover:bg-primary/10";
                          }
                          const optionLower = option.toLowerCase();
                          if (optionLower.includes("outstanding") || optionLower.includes("significantly")) return "border-emerald-600 bg-emerald-50 text-emerald-800 ring-emerald-600/20 dark:bg-emerald-950/50 dark:text-emerald-200 dark:border-emerald-500";
                          if (optionLower.includes("exceeds") || optionLower.includes("exceeded") || optionLower.includes("strongly agree")) return "border-emerald-500 bg-emerald-50 text-emerald-700 ring-emerald-500/20 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-600";
                          if (optionLower.includes("meets") || optionLower.includes("met") || optionLower.includes("agree")) return "border-primary bg-primary/10 text-primary ring-primary/20 dark:bg-primary/20";
                          if (optionLower.includes("needs") || optionLower.includes("partial") || optionLower.includes("neutral")) return "border-amber-500 bg-amber-50 text-amber-700 ring-amber-500/20 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-600";
                          if (optionLower.includes("unacceptable") || optionLower.includes("no improvement") || optionLower.includes("disagree")) return "border-red-500 bg-red-50 text-red-700 ring-red-500/20 dark:bg-red-950/40 dark:text-red-300 dark:border-red-600";
                          return "border-primary bg-primary/10 text-primary ring-primary/20 dark:bg-primary/20";
                        };

                        return (
                          <div key={question.id} className="question-card p-0" data-testid={`question-${question.id}`}>
                            {/* Question header */}
                            <div className="flex items-start gap-4 px-6 pt-5 pb-4">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                <span className="text-xs font-bold text-primary">{qi + 1}</span>
                              </div>
                              <div className="flex-1 pt-0.5">
                                <p className="text-[15px] font-medium text-foreground leading-relaxed">{question.text}</p>
                              </div>
                              {selectedRating && (
                                <Badge variant="outline" className="shrink-0 text-[10px] border-primary/30 text-primary bg-primary/5">
                                  <CheckCircle2 className="h-3 w-3 mr-1" /> Rated
                                </Badge>
                              )}
                            </div>

                            <Separator />

                            {/* Inline Employee Self-Review Response (shown during HR Review) */}
                            {selfResp && selfResp.rating && (
                              <div className="mx-6 mt-4 p-3.5 rounded-lg bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200/60 dark:border-blue-800/50" data-testid={`self-review-inline-${question.id}`}>
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                                  <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider">Employee's Self-Review</span>
                                </div>
                                <div className="flex items-center gap-3 flex-wrap">
                                  <Badge className="bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700 text-xs hover:bg-blue-100">
                                    {selfResp.rating}
                                  </Badge>
                                  {selfResp.starScore > 0 && (
                                    <span className="flex items-center gap-0.5">
                                      {[...Array(5)].map((_, i) => (
                                        <Star key={i} size={12} className={i < selfResp.starScore ? "text-amber-400 fill-amber-400" : "text-border"} />
                                      ))}
                                      <span className="text-[11px] text-amber-600 dark:text-amber-400 ml-1 font-medium">{selfResp.starScore}.0</span>
                                    </span>
                                  )}
                                </div>
                                <div className="mt-2 pt-2 border-t border-blue-200/40 dark:border-blue-800/30">
                                  <span className="text-[11px] font-semibold text-blue-600/70 dark:text-blue-400/70 uppercase tracking-wider">Remarks: </span>
                                  {selfResp.comment ? (
                                    <span className="text-xs text-blue-800 dark:text-blue-200 italic">"{selfResp.comment}"</span>
                                  ) : (
                                    <span className="text-xs text-blue-400 dark:text-blue-500 italic">No remarks provided</span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Rating Pills */}
                            <div className="px-6 py-4">
                              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 block">
                                Performance Rating
                              </Label>
                              <div className="grid grid-cols-2 lg:grid-cols-5 gap-2.5" data-testid={`rating-group-${question.id}`}>
                                {ratingOptions.map((option, optIndex) => {
                                  const isSelected = selectedRating === option;
                                  const autoStarScore = ratingOptions.length - optIndex;
                                  return (
                                    <button
                                      key={option}
                                      type="button"
                                      onClick={() => {
                                        setRatings(prev => ({ ...prev, [question.id]: option }));
                                        setStarScores(prev => ({ ...prev, [question.id]: autoStarScore }));
                                      }}
                                      data-selected={isSelected}
                                      className={`rating-pill ${getRatingStyle(option, isSelected)}`}
                                      data-testid={`rating-${question.id}-${option.replace(/\s+/g, '-').toLowerCase()}`}
                                    >
                                      {option}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Star Score & Comment */}
                            <div className="px-6 pb-5 space-y-4">
                              {/* Star Score */}
                              <div className="flex items-center gap-4 p-3.5 rounded-xl bg-muted/50 dark:bg-muted/30 border border-border/50">
                                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                                  Score
                                </Label>
                                <div className="flex items-center gap-1.5">
                                  {[1, 2, 3, 4, 5].map(s => (
                                    <button
                                      key={s}
                                      type="button"
                                      onClick={() => setStarScores(prev => ({ ...prev, [question.id]: s }))}
                                      className="star-btn focus:outline-none p-0.5"
                                      data-filled={s <= currentStars}
                                      data-testid={`star-${question.id}-${s}`}
                                    >
                                      <Star
                                        size={22}
                                        className={`transition-colors duration-150 ${
                                          s <= currentStars
                                            ? "text-amber-400 fill-amber-400"
                                            : "text-border dark:text-muted-foreground/30"
                                        }`}
                                      />
                                    </button>
                                  ))}
                                </div>
                                {currentStars > 0 && (
                                  <span className="text-sm font-semibold text-amber-600 dark:text-amber-400 ml-1">
                                    {currentStars}.0
                                  </span>
                                )}
                              </div>

                              {/* Comment */}
                              <div>
                                <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                  <MessageSquare className="h-3 w-3" /> Comments
                                </Label>
                                <Textarea
                                  placeholder="Share specific examples, achievements, or context to support your rating..."
                                  value={comments[question.id] || ""}
                                  onChange={(e) => setComments(prev => ({ ...prev, [question.id]: e.target.value }))}
                                  className="resize-none min-h-[80px] bg-card border-border/60 focus:border-primary/50 text-sm"
                                  data-testid={`comment-${question.id}`}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )}

            {/* EMPLOYEE COMMENTS step */}
            {status === "Employee Comments" && (
              <>
                {/* Show HR Review data */}
                {appraisal.hrReview?.responses?.length > 0 && (
                  <Card className="border-purple-200 dark:border-purple-800">
                    <CardHeader className="bg-purple-50/50 dark:bg-purple-950/30 border-b">
                      <CardTitle className="text-base text-purple-800 dark:text-purple-300">HR Review Results</CardTitle>
                      <CardDescription>Overall Score: <strong>{appraisal.hrReview.overallScore}/5</strong></CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      {sections.map((section) => (
                        <div key={section.id} className="border-b last:border-0 p-4">
                          <h4 className="font-semibold text-sm text-foreground mb-2">{section.title}</h4>
                          {(section.questions || []).map((q) => {
                            const hrResp = appraisal.hrReview.responses.find(r => r.questionId === q.id);
                            const selfResp = appraisal.selfReview?.responses?.find(r => r.questionId === q.id);
                            return (
                              <div key={q.id} className="ml-4 mb-2 text-sm">
                                <p className="text-foreground">{q.text}</p>
                                <div className="flex gap-4 mt-1 text-xs">
                                  {selfResp?.rating && <span className="text-blue-600">Self: {selfResp.rating}</span>}
                                  {hrResp?.rating && <span className="text-purple-600">HR: {hrResp.rating}</span>}
                                  {hrResp?.starScore > 0 && (
                                    <span className="flex items-center gap-0.5 text-amber-600">
                                      {[...Array(hrResp.starScore)].map((_, i) => <Star key={i} size={10} className="fill-amber-400 text-amber-400" />)}
                                    </span>
                                  )}
                                </div>
                                {hrResp?.comment && <p className="text-xs text-muted-foreground mt-0.5 italic">"{hrResp.comment}"</p>}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <Card className="border-amber-200 dark:border-amber-800">
                  <CardHeader className="bg-amber-50/50 dark:bg-amber-950/30 border-b">
                    <CardTitle className="text-base text-amber-800 dark:text-amber-300 flex items-center gap-2"><User className="h-5 w-5" /> Your Comments</CardTitle>
                    <CardDescription className="text-amber-700 dark:text-amber-400">Review the HR assessment above and share your feedback.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <Textarea
                      placeholder="Share your thoughts on this review, areas of agreement/disagreement, and any additional context..."
                      className="min-h-[140px]"
                      value={employeeComment}
                      onChange={(e) => setEmployeeComment(e.target.value)}
                      data-testid="employee-comments-textarea"
                    />
                    <div className="flex items-start gap-2 p-4 bg-muted rounded-lg border">
                      <input type="checkbox" id="ack" className="mt-1" checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)} data-testid="acknowledge-checkbox" />
                      <label htmlFor="ack" className="text-sm text-muted-foreground cursor-pointer">
                        I acknowledge that I have reviewed this appraisal. My acknowledgment does not necessarily indicate agreement with all ratings.
                      </label>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* HR SIGN-OFF step */}
            {status === "HR Sign-off" && (
              <>
                {/* Summary of all previous steps */}
                <Card>
                  <CardHeader className="bg-muted border-b">
                    <CardTitle className="text-base">Appraisal Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3 text-sm">
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-blue-600" /><span>Self-Review</span></div>
                      <span className="text-muted-foreground">{appraisal.selfReview?.responses?.length || 0} responses submitted</span>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-purple-600" /><span>HR Review</span></div>
                      <span className="text-muted-foreground">Score: {appraisal.hrReview?.overallScore || 0}/5</span>
                    </div>
                    <div className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-amber-600" /><span>Employee Comments</span></div>
                      <span className="text-muted-foreground">{appraisal.employeeComments?.acknowledged ? "Acknowledged" : "Not acknowledged"}</span>
                    </div>
                    {appraisal.employeeComments?.comments && (
                      <div className="p-3 bg-amber-50/50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                        <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 mb-1">Employee said:</p>
                        <p className="text-sm text-amber-700 dark:text-amber-400 italic">"{appraisal.employeeComments.comments}"</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="border-green-200 dark:border-green-800">
                  <CardHeader className="bg-green-50/50 dark:bg-green-950/30 border-b">
                    <CardTitle className="text-base text-green-800 dark:text-green-300 flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> HR Sign-off</CardTitle>
                    <CardDescription className="text-green-700 dark:text-green-400">Finalize the appraisal with a rating and comments.</CardDescription>
                  </CardHeader>
                  <CardContent className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Final Rating (1-5)</Label>
                        <Input
                          type="number" min="1" max="5" step="0.1"
                          placeholder="e.g., 4.2"
                          value={finalRating}
                          onChange={(e) => setFinalRating(e.target.value)}
                          data-testid="final-rating-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Signed By</Label>
                        <Input value="Jane Doe" disabled className="bg-muted" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Final Comments</Label>
                      <Textarea
                        placeholder="Closing remarks, improvement areas, commendations..."
                        className="min-h-[100px]"
                        value={signOffComments}
                        onChange={(e) => setSignOffComments(e.target.value)}
                        data-testid="signoff-comments"
                      />
                    </div>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Read-only: Completed — show full summary */}
            {isCompleted && sections.length > 0 && (
              <Card>
                <CardHeader className="bg-muted border-b"><CardTitle className="text-base">Full Review Details</CardTitle></CardHeader>
                <CardContent className="p-0">
                  {sections.map((section) => (
                    <div key={section.id} className="border-b last:border-0 p-4">
                      <h4 className="font-semibold text-sm text-foreground mb-2">{section.title} {section.weight > 0 && <span className="text-muted-foreground font-normal">({section.weight}%)</span>}</h4>
                      {(section.questions || []).map((q) => {
                        const self = appraisal.selfReview?.responses?.find(r => r.questionId === q.id);
                        const hr = appraisal.hrReview?.responses?.find(r => r.questionId === q.id);
                        return (
                          <div key={q.id} className="ml-4 mb-3 text-sm border-l-2 border-border pl-3">
                            <p className="text-foreground font-medium">{q.text}</p>
                            <div className="flex gap-6 mt-1 text-xs">
                              <span className="text-blue-600">Self: {self?.rating || "—"}</span>
                              <span className="text-purple-600">HR: {hr?.rating || "—"}</span>
                              {hr?.starScore > 0 && <span className="text-amber-600">Score: {hr.starScore}/5</span>}
                            </div>
                            {hr?.comment && <p className="text-xs text-muted-foreground italic mt-0.5">"{hr.comment}"</p>}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AppraisalEditor;
